import { Box, Stack } from '@mui/material'
import type { FormContextValue } from 'dynamic-uniforms'
import { connectField, FormContext, getValueByPath, useFormContext } from 'dynamic-uniforms'
import { ComponentType, createContext, ReactNode, useContext, useMemo } from 'react'
import BooleanRadioField from './BooleanRadioField'
import CheckboxOptionsField from './CheckboxOptionsField'
import { useCiboAutoForm } from './CiboAutoFormContext'
import { DataGridField } from './DataGridField'
import { FieldLabel } from './FieldLabel'
import FileUploadField from './FileUploadField'
import ListField from './ListField'
import { NumberField } from './NumberField'
import RadioField from './RadioField'
import SelectField from './SelectField'
import TextField from './TextField'
import { useCiboAutoFormDisplays } from './useCiboAutoFormDisplays'

export type NestFieldProps = {
  children?: ReactNode
  fields?: string[]
  fullWidth?: boolean
  itemProps?: object
  label?: string
  margin?: any
  helperText?: string
  name: string
  value?: any
  onChange?: any
  error?: any
  disabled?: boolean
  readOnly?: boolean
  field?: any
  [key: string]: any
}

function Nest(props: NestFieldProps) {
  const {
    children,
    fields,
    itemProps,
    name,
    label,
    ..._ // remaining props unused
  } = props

  const uiParams = props?.['ui-params'] || {}

  const ctx = useFormContext()
  const { renderDisplay } = useCiboAutoForm()

  // Provide nested context so child fields resolve relative to this object
  const nestedContext = useMemo<FormContextValue>(
    () => ({
      ...ctx,
      namePrefix: name,
    }),
    [ctx, name]
  )

  const field = props.field || {}
  const displays = useCiboAutoFormDisplays({
    schema: { ...field, properties: field.properties || {} },
    model: getValueByPath(ctx.model, name) as any,
  })

  return (
    <FormContext.Provider value={nestedContext}>
      {label && <FieldLabel label={label} required={field.required} />}

      <Box paddingBottom={3} paddingLeft={uiParams.nestIndent ? 2 : 0}>
        {children || (
          <Stack spacing={1}>
            {displays?.map((field: string | any) =>
              renderDisplay ? (
                renderDisplay({ field, itemProps })
              ) : typeof field === 'string' ? (
                <AutoField key={field} name={field} {...itemProps} />
              ) : null
            )}
          </Stack>
        )}
      </Box>
    </FormContext.Provider>
  )
}

const NestField = connectField<NestFieldProps>(Nest)

// Component detector context for custom field overrides (used by programs package)
type ComponentDetector = (props: any, uniforms?: any) => ComponentType<any> | undefined | null
const ComponentDetectorContext = createContext<ComponentDetector | null>(null)

function defaultComponentDetector(props: any): ComponentType<any> | null {
  // component: null explicitly hides the field
  if (props.component === null) return null
  if (props.component) {
    return props.component
  }

  const uiParams = props?.['ui-params'] || {}

  if (uiParams.layout === 'radio') return RadioField
  if (uiParams.layout === 'checkbox') return CheckboxOptionsField
  if (uiParams.layout === 'select') return SelectField
  if (props.widget === 'FileUploadField') return FileUploadField
  if (props.fieldType === String && !!props.oneOf) return SelectField

  switch (props.fieldType) {
    case Array:
      return uiParams.layout === 'table' ? DataGridField : ListField
    case Boolean:
      return BooleanRadioField
    case Number:
      return NumberField
    case Object:
      return NestField as ComponentType<any>
    case String:
      return TextField
  }

  return null
}

export function AutoField(props: { name: string; [key: string]: any }) {
  const ctx = useFormContext()
  const detector = useContext(ComponentDetectorContext)

  const fullName = ctx.namePrefix
    ? props.name
      ? `${ctx.namePrefix}.${props.name}`
      : ctx.namePrefix
    : props.name

  if (!ctx.bridge.hasField(fullName)) return null

  const fieldType = ctx.bridge.getType(fullName)
  const field = ctx.bridge.getField(fullName)
  const uiParams = (field as any)['ui-params'] || {}

  const schemaProps = ctx.bridge.getProps(fullName)
  const required = schemaProps.required ?? false

  const detectorProps = {
    ...props,
    fieldType,
    'ui-params': uiParams,
    oneOf: field.oneOf,
    anyOf: field.anyOf,
    widget: (field.uniforms as any)?.widget ?? uiParams.widget,
    component: (field.uniforms as any)?.component,
  }

  // Try custom detector, then default
  let Component = detector?.(detectorProps, {})
  if (!Component) {
    Component = defaultComponentDetector(detectorProps)
  }

  if (!Component) return null

  return <Component {...props} required={required} />
}

// Expose detector context for override (used by programs package)
AutoField.componentDetectorContext = ComponentDetectorContext
AutoField.defaultComponentDetector = defaultComponentDetector

export default AutoField
